import {computed, effect, inject, Injectable, signal} from '@angular/core';
import Keycloak from 'keycloak-js';
import {AppRoutes} from '../models/routes.model';
import {AuthUserModel} from '../models/auth-user.model';
import {
    clearKeycloakTokens,
    storeKeycloakTokens,
} from '../config/keycloak-session.storage';
import {
    KEYCLOAK_EVENT_SIGNAL,
    KeycloakEventType,
    typeEventArgs,
} from 'keycloak-angular';
import {KeycloakInitStateService} from './keycloak-init-state.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly currentUser = signal<AuthUserModel | null>(null);
    private readonly keycloakEvent = inject(KEYCLOAK_EVENT_SIGNAL);
    private readonly initState = inject(KeycloakInitStateService);
    /**
     * Stato di autenticazione Keycloak come signal: `user` arriva dopo, in modo
     * asincrono, mentre qui il valore è disponibile subito dopo l'init (che
     * precede il bootstrap) e segue login, refresh e logout.
     */
    private readonly authenticatedSignal = signal<boolean>(false);

    readonly user = computed(() => this.currentUser());
    readonly isAuthenticatedSignal = computed(() => this.authenticatedSignal());
    readonly displayName = computed(() => {
        const user = this.currentUser();
        if (!user) {
            return '';
        }

        const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        return fullName || user.fullName || user.username || user.email || '';
    });

    readonly initials = computed(() => {
        const user = this.currentUser();
        if (!user) {
            return '';
        }

        const first = user.firstName?.charAt(0) ?? user.fullName?.charAt(0) ?? user.username?.charAt(0) ?? '';
        const last = user.lastName?.charAt(0) ?? '';
        return `${first}${last}`.toUpperCase();
    });

    constructor(private readonly keycloak: Keycloak) {
      // L'init di Keycloak è già concluso al bootstrap: lo stato iniziale si
      // legge direttamente, senza aspettare che l'effect processi l'evento Ready.
      this.authenticatedSignal.set(this.keycloak.authenticated ?? false);

      // Lo stato iniziale può già essere autenticato: l'init ha appena
      // rinnovato i token restituiti dalla scheda.
      if (this.keycloak.authenticated) {
          this.persistTokens();
      }

      effect(() => {
          const keycloakEvent = this.keycloakEvent();

          if (keycloakEvent.type === KeycloakEventType.Ready) {
              const authenticated = typeEventArgs<boolean>(keycloakEvent.args);
              this.authenticatedSignal.set(authenticated);
              if (authenticated) {
                  this.persistTokens();
                  void this.loadCurrentUser();
              } else {
                  clearKeycloakTokens();
                  this.currentUser.set(null);
              }
          }

          if (
              keycloakEvent.type === KeycloakEventType.AuthSuccess ||
              keycloakEvent.type === KeycloakEventType.AuthRefreshSuccess
          ) {
              this.authenticatedSignal.set(true);
              // Ogni rinnovo produce token nuovi: senza riscriverli, al
              // ricaricamento successivo si tornerebbe a quelli vecchi.
              this.persistTokens();
              void this.loadCurrentUser();
          }

          if (
              keycloakEvent.type === KeycloakEventType.AuthLogout ||
              keycloakEvent.type === KeycloakEventType.AuthRefreshError
          ) {
              this.authenticatedSignal.set(false);
              clearKeycloakTokens();
              this.currentUser.set(null);
          }
      });
    }

    /**
     * Conserva i token per la durata della scheda, così un ricaricamento non
     * riporta l'utente allo stato anonimo. Vedi `keycloak-session.storage.ts`
     * per il motivo per cui non si usa il controllo silenzioso della sessione.
     */
    private persistTokens(): void {
        storeKeycloakTokens({
            token: this.keycloak.token,
            refreshToken: this.keycloak.refreshToken,
            idToken: this.keycloak.idToken,
        });
    }

    /**
     * Avvia il login su Keycloak. `returnUrl` e' la rotta su cui atterrare al
     * ritorno (deep link richiesto prima dell'autenticazione); senza, si va
     * sulla vetrina piani.
     */
    async login(returnUrl?: string): Promise<void> {
        // Ogni chiamata qui è un'azione esplicita (pulsante "Accedi", link di
        // conferma email, riprova dall'avviso): il precedente fallimento non
        // deve bloccarla. I guard, invece, non arrivano qui finché è segnato.
        this.initState.reset();

        const target =
            returnUrl ??
            `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.DASHBOARD}`;

        await this.keycloak.login({
            redirectUri: `${window.location.origin}${target.startsWith('/') ? target : `/${target}`}`,
        });
    }

    /**
     * Logout Keycloak. `returnUrl` è la rotta pubblica su cui tornare (es. il
     * checkout da cui l'utente ha premuto "Esci"); senza, la pagina di login.
     */
    async logout(returnUrl?: string): Promise<void> {
        // Prima del redirect: dopo, questo codice non viene più eseguito.
        clearKeycloakTokens();

        const target = returnUrl
            ? returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`
            : `/${AppRoutes.AUTH}/${AppRoutes.AUTH_LOGIN}`;

        await this.keycloak.logout({
            redirectUri: `${window.location.origin}${target}`,
        });
    }

    isAuthenticated(): boolean {
        // `authenticated` e' undefined finche' l'init di Keycloak non e'
        // completato, malgrado la firma lo dichiari boolean.
        return this.keycloak.authenticated ?? false;
    }

    get token(): string | undefined {
        return this.keycloak.token;
    }

    async loadCurrentUser(): Promise<void> {
        if (!this.isAuthenticated()) {
            this.currentUser.set(null);
            return;
        }

        const tokenClaims = this.keycloak.tokenParsed as Record<string, unknown> | undefined;
        const baseUser: AuthUserModel = {
            id: typeof tokenClaims?.['sub'] === 'string' ? tokenClaims['sub'] : undefined,
            username: typeof tokenClaims?.['preferred_username'] === 'string'
                ? tokenClaims['preferred_username']
                : undefined,
            firstName: typeof tokenClaims?.['given_name'] === 'string'
                ? tokenClaims['given_name']
                : undefined,
            lastName: typeof tokenClaims?.['family_name'] === 'string'
                ? tokenClaims['family_name']
                : undefined,
            fullName: typeof tokenClaims?.['name'] === 'string'
                ? tokenClaims['name']
                : undefined,
            email: typeof tokenClaims?.['email'] === 'string'
                ? tokenClaims['email']
                : undefined,
        };

        this.currentUser.set(baseUser);

        try {
            const profile = await this.keycloak.loadUserProfile();
            this.currentUser.set({
                id: baseUser.id,
                username: profile.username ?? baseUser.username,
                firstName: profile.firstName ?? baseUser.firstName,
                lastName: profile.lastName ?? baseUser.lastName,
                fullName: baseUser.fullName,
                email: profile.email ?? baseUser.email,
            });
        } catch {
            // Fall back to token claims only if the profile endpoint is unavailable.
        }
    }
}
